import { REGISTERED_BREAKPOINTS } from '#pplx/frontend/design/tokens/spacingTokens.js';
// ---------------------------------------------------------------------------
// Discrimination helper
// ---------------------------------------------------------------------------
const BREAKPOINT_KEY_SET = new Set(['default', ...REGISTERED_BREAKPOINTS]);
export function isResponsiveObject(value) {
    return Object.keys(value).some((key) => BREAKPOINT_KEY_SET.has(key));
}
// ---------------------------------------------------------------------------
// Breakpoint prefix helper
// ---------------------------------------------------------------------------
/**
 * Prepends a Tailwind breakpoint prefix to a base class name.
 * For 'default', returns the class as-is; for others, prepends `bp:`.
 *
 * The responsive variant classes are guaranteed to exist in the CSS output
 * because they are covered by the Tailwind safelist generated from
 * the shared design tokens (see tailwind-config/src/safelist.ts).
 */
function bpClass(bp, baseClass) {
    if (bp === 'default')
        return baseClass;
    return `${bp}:${baseClass}`;
}
// ---------------------------------------------------------------------------
// Padding base class maps (default breakpoint)
// ---------------------------------------------------------------------------
const paddingClasses = {
    '2xs': 'p-0.5',
    xs: 'p-1',
    sm: 'p-2',
    md: 'p-4',
    ml: 'p-6',
    lg: 'p-8',
    xl: 'p-12',
};
const paddingTopClasses = {
    '2xs': 'pt-0.5',
    xs: 'pt-1',
    sm: 'pt-2',
    md: 'pt-4',
    ml: 'pt-6',
    lg: 'pt-8',
    xl: 'pt-12',
};
const paddingBottomClasses = {
    '2xs': 'pb-0.5',
    xs: 'pb-1',
    sm: 'pb-2',
    md: 'pb-4',
    ml: 'pb-6',
    lg: 'pb-8',
    xl: 'pb-12',
};
const paddingLeftClasses = {
    '2xs': 'pl-0.5',
    xs: 'pl-1',
    sm: 'pl-2',
    md: 'pl-4',
    ml: 'pl-6',
    lg: 'pl-8',
    xl: 'pl-12',
};
const paddingRightClasses = {
    '2xs': 'pr-0.5',
    xs: 'pr-1',
    sm: 'pr-2',
    md: 'pr-4',
    ml: 'pr-6',
    lg: 'pr-8',
    xl: 'pr-12',
};
// ---------------------------------------------------------------------------
// Margin base class maps (default breakpoint)
// ---------------------------------------------------------------------------
const marginClasses = {
    '2xs': 'm-0.5',
    xs: 'm-1',
    sm: 'm-2',
    md: 'm-4',
    ml: 'm-6',
    lg: 'm-8',
    xl: 'm-12',
};
const marginTopClasses = {
    '2xs': 'mt-0.5',
    xs: 'mt-1',
    sm: 'mt-2',
    md: 'mt-4',
    ml: 'mt-6',
    lg: 'mt-8',
    xl: 'mt-12',
};
const marginBottomClasses = {
    '2xs': 'mb-0.5',
    xs: 'mb-1',
    sm: 'mb-2',
    md: 'mb-4',
    ml: 'mb-6',
    lg: 'mb-8',
    xl: 'mb-12',
};
const marginLeftClasses = {
    '2xs': 'ml-0.5',
    xs: 'ml-1',
    sm: 'ml-2',
    md: 'ml-4',
    ml: 'ml-6',
    lg: 'ml-8',
    xl: 'ml-12',
};
const marginRightClasses = {
    '2xs': 'mr-0.5',
    xs: 'mr-1',
    sm: 'mr-2',
    md: 'mr-4',
    ml: 'mr-6',
    lg: 'mr-8',
    xl: 'mr-12',
};
// ---------------------------------------------------------------------------
// Rounded base class maps (default breakpoint)
// ---------------------------------------------------------------------------
const roundedClasses = {
    none: 'rounded-none',
    sm: 'rounded-xs',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    '2xl': 'rounded-2xl',
    '3xl': 'rounded-3xl',
    full: 'rounded-full',
};
const roundedTopClasses = {
    none: 'rounded-t-none',
    sm: 'rounded-t-xs',
    md: 'rounded-t-md',
    lg: 'rounded-t-lg',
    xl: 'rounded-t-xl',
    '2xl': 'rounded-t-2xl',
    '3xl': 'rounded-t-3xl',
    full: 'rounded-t-full',
};
const roundedBottomClasses = {
    none: 'rounded-b-none',
    sm: 'rounded-b-xs',
    md: 'rounded-b-md',
    lg: 'rounded-b-lg',
    xl: 'rounded-b-xl',
    '2xl': 'rounded-b-2xl',
    '3xl': 'rounded-b-3xl',
    full: 'rounded-b-full',
};
const roundedLeftClasses = {
    none: 'rounded-l-none',
    sm: 'rounded-l-xs',
    md: 'rounded-l-md',
    lg: 'rounded-l-lg',
    xl: 'rounded-l-xl',
    '2xl': 'rounded-l-2xl',
    '3xl': 'rounded-l-3xl',
    full: 'rounded-l-full',
};
const roundedRightClasses = {
    none: 'rounded-r-none',
    sm: 'rounded-r-xs',
    md: 'rounded-r-md',
    lg: 'rounded-r-lg',
    xl: 'rounded-r-xl',
    '2xl': 'rounded-r-2xl',
    '3xl': 'rounded-r-3xl',
    full: 'rounded-r-full',
};
const roundedTLClasses = {
    none: 'rounded-tl-none',
    sm: 'rounded-tl-xs',
    md: 'rounded-tl-md',
    lg: 'rounded-tl-lg',
    xl: 'rounded-tl-xl',
    '2xl': 'rounded-tl-2xl',
    '3xl': 'rounded-tl-3xl',
    full: 'rounded-tl-full',
};
const roundedTRClasses = {
    none: 'rounded-tr-none',
    sm: 'rounded-tr-xs',
    md: 'rounded-tr-md',
    lg: 'rounded-tr-lg',
    xl: 'rounded-tr-xl',
    '2xl': 'rounded-tr-2xl',
    '3xl': 'rounded-tr-3xl',
    full: 'rounded-tr-full',
};
const roundedBLClasses = {
    none: 'rounded-bl-none',
    sm: 'rounded-bl-xs',
    md: 'rounded-bl-md',
    lg: 'rounded-bl-lg',
    xl: 'rounded-bl-xl',
    '2xl': 'rounded-bl-2xl',
    '3xl': 'rounded-bl-3xl',
    full: 'rounded-bl-full',
};
const roundedBRClasses = {
    none: 'rounded-br-none',
    sm: 'rounded-br-xs',
    md: 'rounded-br-md',
    lg: 'rounded-br-lg',
    xl: 'rounded-br-xl',
    '2xl': 'rounded-br-2xl',
    '3xl': 'rounded-br-3xl',
    full: 'rounded-br-full',
};
// ---------------------------------------------------------------------------
// Registered breakpoint set (for runtime validation)
// ---------------------------------------------------------------------------
const REGISTERED_BP_SET = new Set(['default', ...REGISTERED_BREAKPOINTS]);
function isRegisteredBreakpoint(bp) {
    return REGISTERED_BP_SET.has(bp);
}
// ---------------------------------------------------------------------------
// Per-side resolvers (internal)
// ---------------------------------------------------------------------------
function resolvePaddingSides(bp, obj) {
    const top = obj.top ?? obj.y;
    const bottom = obj.bottom ?? obj.y;
    const left = obj.left ?? obj.x;
    const right = obj.right ?? obj.x;
    return [
        top ? bpClass(bp, paddingTopClasses[top]) : '',
        bottom ? bpClass(bp, paddingBottomClasses[bottom]) : '',
        left ? bpClass(bp, paddingLeftClasses[left]) : '',
        right ? bpClass(bp, paddingRightClasses[right]) : '',
    ]
        .filter(Boolean)
        .join(' ');
}
function resolveMarginSides(bp, obj) {
    const top = obj.top ?? obj.y;
    const bottom = obj.bottom ?? obj.y;
    const left = obj.left ?? obj.x;
    const right = obj.right ?? obj.x;
    return [
        top ? bpClass(bp, marginTopClasses[top]) : '',
        bottom ? bpClass(bp, marginBottomClasses[bottom]) : '',
        left ? bpClass(bp, marginLeftClasses[left]) : '',
        right ? bpClass(bp, marginRightClasses[right]) : '',
    ]
        .filter(Boolean)
        .join(' ');
}
function resolveRoundedSides(bp, obj) {
    const hasCorners = obj.topLeft != null ||
        obj.topRight != null ||
        obj.bottomLeft != null ||
        obj.bottomRight != null;
    if (!hasCorners) {
        return [
            obj.top ? bpClass(bp, roundedTopClasses[obj.top]) : '',
            obj.bottom ? bpClass(bp, roundedBottomClasses[obj.bottom]) : '',
            obj.left ? bpClass(bp, roundedLeftClasses[obj.left]) : '',
            obj.right ? bpClass(bp, roundedRightClasses[obj.right]) : '',
        ]
            .filter(Boolean)
            .join(' ');
    }
    const tl = obj.topLeft ?? obj.top ?? obj.left;
    const tr = obj.topRight ?? obj.top ?? obj.right;
    const bl = obj.bottomLeft ?? obj.bottom ?? obj.left;
    const br = obj.bottomRight ?? obj.bottom ?? obj.right;
    return [
        tl ? bpClass(bp, roundedTLClasses[tl]) : '',
        tr ? bpClass(bp, roundedTRClasses[tr]) : '',
        bl ? bpClass(bp, roundedBLClasses[bl]) : '',
        br ? bpClass(bp, roundedBRClasses[br]) : '',
    ]
        .filter(Boolean)
        .join(' ');
}
// ---------------------------------------------------------------------------
// Public resolvers
// ---------------------------------------------------------------------------
export function resolveBoxPaddingClasses(padding) {
    if (!padding)
        return '';
    if (typeof padding === 'string')
        return paddingClasses[padding] ?? '';
    if (!isResponsiveObject(padding)) {
        return resolvePaddingSides('default', padding);
    }
    const responsive = padding;
    return Object.entries(responsive)
        .map(([bp, val]) => {
        if (!val || !isRegisteredBreakpoint(bp))
            return '';
        if (typeof val === 'string')
            return bpClass(bp, paddingClasses[val]);
        return resolvePaddingSides(bp, val);
    })
        .filter(Boolean)
        .join(' ');
}
export function resolveBoxMarginClasses(margin) {
    if (!margin)
        return '';
    if (typeof margin === 'string')
        return marginClasses[margin] ?? '';
    if (!isResponsiveObject(margin)) {
        return resolveMarginSides('default', margin);
    }
    const responsive = margin;
    return Object.entries(responsive)
        .map(([bp, val]) => {
        if (!val || !isRegisteredBreakpoint(bp))
            return '';
        if (typeof val === 'string')
            return bpClass(bp, marginClasses[val]);
        return resolveMarginSides(bp, val);
    })
        .filter(Boolean)
        .join(' ');
}
export function resolveBoxRoundedClasses(rounded) {
    if (!rounded)
        return '';
    if (typeof rounded === 'string')
        return roundedClasses[rounded] ?? '';
    if (!isResponsiveObject(rounded)) {
        return resolveRoundedSides('default', rounded);
    }
    const responsive = rounded;
    return Object.entries(responsive)
        .map(([bp, val]) => {
        if (!val || !isRegisteredBreakpoint(bp))
            return '';
        if (typeof val === 'string')
            return bpClass(bp, roundedClasses[val]);
        return resolveRoundedSides(bp, val);
    })
        .filter(Boolean)
        .join(' ');
}
//# sourceMappingURL=boxResponsiveClasses.js.map